/** Sponsor Information section: paired name + address field types */
export const SPONSOR_FIELD_TYPES = {
  sponsor: {
    key: 'sponsor',
    pairLabel: 'Sponsor',
    nameLabel: 'Sponsor Name',
    addressLabel: 'Sponsor Legal Address',
    entriesKey: 'sponsor_entries',
    legacyNameKey: 'sponsor_name',
    legacyAddressKey: 'sponsor_legal_address',
    required: true,
  },
  co_sponsor: {
    key: 'co_sponsor',
    pairLabel: 'Co-Sponsor',
    nameLabel: 'Co-Sponsor Name',
    addressLabel: 'Co-Sponsor Legal Address',
    entriesKey: 'co_sponsor_entries',
    legacyNameKey: 'co_sponsor_name',
    legacyAddressKey: 'co_sponsor_legal_address',
  },
  local_sponsor: {
    key: 'local_sponsor',
    pairLabel: 'Local Sponsor',
    nameLabel: 'Local Sponsor Name',
    addressLabel: 'Local Sponsor Address',
    entriesKey: 'local_sponsor_entries',
    legacyNameKey: 'local_sponsor_name',
    legacyAddressKey: 'local_sponsor_address',
  },
  device_manufacturer: {
    key: 'device_manufacturer',
    pairLabel: 'Device Manufacturer',
    nameLabel: 'Device Manufacturer Name',
    addressLabel: 'Device Manufacturer Address',
    entriesKey: 'device_manufacturer_entries',
    legacyNameKey: 'device_manufacturer_name',
    legacyAddressKey: 'device_manufacturer_address',
  },
  collaborator: {
    key: 'collaborator',
    pairLabel: 'Collaborator',
    nameLabel: 'Collaborator Name',
    addressLabel: 'Collaborator Address',
    entriesKey: 'collaborator_entries',
    legacyNameKey: null,
    legacyAddressKey: null,
  },
};

/** All types (load/save legacy data). */
export const SPONSOR_FIELD_TYPE_LIST = Object.values(SPONSOR_FIELD_TYPES);

/** All sponsor-information types available in the dropdown. */
export const SPONSOR_ADDABLE_TYPE_LIST = SPONSOR_FIELD_TYPE_LIST;

export const createEmptySponsorEntry = (type = 'sponsor') => ({
  id: `entry-${Date.now()}-${Math.random().toString(36).slice(2, 9)}`,
  type,
  name: '',
  address: '',
});

export const ensureRequiredSponsorEntry = (entries = []) => {
  const hasSponsor = entries.some((e) => e.type === 'sponsor');
  if (hasSponsor) return entries;
  return [createEmptySponsorEntry('sponsor'), ...entries];
};

export const countEntriesByType = (entries, type) =>
  entries.filter((e) => e.type === type).length;

/** Stable display order for sponsor section groups. */
export const SPONSOR_TYPE_ORDER = SPONSOR_ADDABLE_TYPE_LIST.map((d) => d.key);

/** Keep entries grouped by type (Sponsor block, then Co-Sponsor, etc.). */
export const sortSponsorEntriesByType = (entries = []) => {
  const result = [];
  SPONSOR_TYPE_ORDER.forEach((typeKey) => {
    entries.filter((e) => e.type === typeKey).forEach((e) => result.push(e));
  });
  return result;
};

/** Insert after the last row of the same type; new types slot in section order. */
export const insertSponsorEntry = (entries = [], newEntry) => {
  const type = newEntry.type;
  let lastSameTypeIndex = -1;
  for (let i = entries.length - 1; i >= 0; i -= 1) {
    if (entries[i].type === type) {
      lastSameTypeIndex = i;
      break;
    }
  }
  if (lastSameTypeIndex >= 0) {
    const next = [...entries];
    next.splice(lastSameTypeIndex + 1, 0, newEntry);
    return next;
  }
  const typeOrder = SPONSOR_TYPE_ORDER.indexOf(type);
  let insertAt = entries.length;
  for (let i = 0; i < entries.length; i += 1) {
    const idx = SPONSOR_TYPE_ORDER.indexOf(entries[i].type);
    if (idx > typeOrder) {
      insertAt = i;
      break;
    }
  }
  const next = [...entries];
  next.splice(insertAt, 0, newEntry);
  return next;
};

export const groupSponsorEntriesByType = (entries = []) =>
  SPONSOR_TYPE_ORDER.map((typeKey) => ({
    type: typeKey,
    entries: entries.filter((e) => e.type === typeKey),
  })).filter((g) => g.entries.length > 0);

const trim = (v) => (v == null ? '' : String(v).trim());

const pairFromLegacy = (name, address) => {
  const n = trim(name);
  const a = trim(address);
  if (!n && !a) return [];
  return [{ name: n, address: a }];
};

const pairsFromEntries = (entries) => {
  if (!Array.isArray(entries)) return [];
  return entries
    .map((e) => ({ name: trim(e?.name), address: trim(e?.address) }))
    .filter((e) => e.name || e.address);
};

/** Build UI rows from saved administrative info (arrays or legacy flat fields). */
export const sponsorEntriesFromInitialData = (data = {}) => {
  if (!data) return [];

  const rows = [];
  let idCounter = 0;
  const pushType = (typeKey, pairs) => {
    pairs.forEach((pair) => {
      rows.push({
        id: `entry-${idCounter += 1}`,
        type: typeKey,
        name: pair.name,
        address: pair.address,
      });
    });
  };

  SPONSOR_FIELD_TYPE_LIST.forEach((def) => {
    const fromArray = pairsFromEntries(data[def.entriesKey]);
    if (fromArray.length > 0) {
      pushType(def.key, fromArray);
      return;
    }
    if (def.legacyNameKey) {
      pushType(
        def.key,
        pairFromLegacy(data[def.legacyNameKey], data[def.legacyAddressKey])
      );
      return;
    }
    if (def.key === 'collaborator') {
      const legacyCollab = data.collaborators;
      if (Array.isArray(legacyCollab) && legacyCollab.length > 0) {
        legacyCollab.forEach((c) => {
          if (typeof c === 'string') {
            const s = trim(c);
            if (s) rows.push({ id: `entry-${idCounter += 1}`, type: 'collaborator', name: s, address: '' });
          } else if (c && (c.name || c.address || c.value)) {
            rows.push({
              id: `entry-${idCounter += 1}`,
              type: 'collaborator',
              name: trim(c.name || c.label || c.value),
              address: trim(c.address || ''),
            });
          }
        });
      }
    }
  });

  return sortSponsorEntriesByType(rows);
};

/** Group UI rows into API payload (arrays + legacy flat fields for backward compatibility). */
export const serializeSponsorEntries = (entries = []) => {
  const grouped = {};
  SPONSOR_FIELD_TYPE_LIST.forEach((def) => {
    grouped[def.entriesKey] = [];
  });

  entries.forEach((row) => {
    const def = SPONSOR_FIELD_TYPES[row.type];
    if (!def) return;
    const name = trim(row.name);
    const address = trim(row.address);
    if (!name && !address) return;
    grouped[def.entriesKey].push({ name, address });
  });

  const payload = { ...grouped };

  SPONSOR_FIELD_TYPE_LIST.forEach((def) => {
    if (!def.legacyNameKey) return;
    const list = grouped[def.entriesKey];
    const first = list[0] || { name: '', address: '' };
    payload[def.legacyNameKey] = first.name;
    payload[def.legacyAddressKey] = first.address;
  });

  // Legacy string list: collaborator names only (full pairs live in collaborator_entries)
  payload.collaborators = (grouped.collaborator_entries || [])
    .map((c) => trim(c.name))
    .filter(Boolean);

  return payload;
};

export const getSponsorFieldDef = (typeKey) => SPONSOR_FIELD_TYPES[typeKey];

/** First non-empty name from entry array or legacy flat field. */
export const firstEntryName = (entries, legacyName) => {
  const pairs = pairsFromEntries(entries);
  if (pairs.length > 0 && pairs[0].name) return pairs[0].name;
  return trim(legacyName);
};

/** First non-empty address from entry array or legacy flat field. */
export const firstEntryAddress = (entries, legacyAddress) => {
  const pairs = pairsFromEntries(entries);
  if (pairs.length > 0 && pairs[0].address) return pairs[0].address;
  return trim(legacyAddress);
};

/** Resolve sponsor section fields from API document (arrays + legacy flat fields). */
export const resolveAdminSponsorFields = (adminInfo = {}) => {
  if (!adminInfo || typeof adminInfo !== 'object') {
    return {
      sponsorName: '',
      sponsorAddress: '',
      coSponsorName: '',
      coSponsorAddress: '',
      localSponsorName: '',
      localSponsorAddress: '',
      deviceManufacturerName: '',
      deviceManufacturerAddress: '',
    };
  }
  return {
    sponsorName: firstEntryName(adminInfo.sponsor_entries, adminInfo.sponsor_name || adminInfo.sponsor),
    sponsorAddress: firstEntryAddress(
      adminInfo.sponsor_entries,
      adminInfo.sponsor_legal_address || adminInfo.sponsor_address || adminInfo.sponsor_location
    ),
    coSponsorName: firstEntryName(adminInfo.co_sponsor_entries, adminInfo.co_sponsor_name || adminInfo.co_sponsor),
    coSponsorAddress: firstEntryAddress(
      adminInfo.co_sponsor_entries,
      adminInfo.co_sponsor_legal_address || adminInfo.co_sponsor_address
    ),
    localSponsorName: firstEntryName(
      adminInfo.local_sponsor_entries,
      adminInfo.local_sponsor_name || adminInfo.local_affiliate_name
    ),
    localSponsorAddress: firstEntryAddress(
      adminInfo.local_sponsor_entries,
      adminInfo.local_sponsor_address || adminInfo.local_affiliate_address
    ),
    deviceManufacturerName: firstEntryName(adminInfo.device_manufacturer_entries, adminInfo.device_manufacturer_name),
    deviceManufacturerAddress: firstEntryAddress(
      adminInfo.device_manufacturer_entries,
      adminInfo.device_manufacturer_address
    ),
  };
};

export const formatNameAddressPairsForDisplay = (entries, legacyName, legacyAddress) => {
  const pairs = pairsFromEntries(entries);
  const source = pairs.length > 0 ? pairs : pairFromLegacy(legacyName, legacyAddress);
  return source
    .map((p) => {
      const n = trim(p.name);
      const a = trim(p.address);
      if (n && a) return `${n}\n${a}`;
      return n || a;
    })
    .filter(Boolean)
    .join('\n\n');
};
